import { useMutation, useQuery } from '@tanstack/react-query';
import { message } from 'antd';
import { queryClient } from '@src/services/common/query-client';
import {
  postStableVersion,
  getDataByConditions,
  getCriticalEvent,
  createCriticalEvent,
  updateCriticalEvent,
  getDimension,
  createDimension,
  updateDimension,
  postUserJourneysFilter,
  getPatterns,
  postPatternQuery,
  postPatternsQuery,
  postMilestoneQuery,
  type PatternQueryRequest,
  type PatternsQueryRequest,
  type MilestoneQueryRequest,
  type CreateCriticalEventRequest,
  type UpdateCriticalEventRequest,
  type CreateDimensionRequest,
  type UpdateDimensionRequest,
} from '@src/services/query/applang/queries';
import type { UserJourneyFilterRequest, PatternsRequest } from '@shared/types';
import {
  getEventNames,
  getTagKeys,
  getTagValues,
  type GetEventNamesRequest,
  type GetTagKeysRequest,
  type GetTagValuesRequest,
} from '@src/services/query/metadata';

export const usePostStableVersion = () => {
  return useMutation(async (payload: { customerId: number; data: any }) => {
    const { customerId, data } = payload;
    const res = await postStableVersion(customerId, data);
    void queryClient.invalidateQueries(['get_journey_files']);
    return res?.data?.data || {};
  });
};

export const useGetDataByConditions = (payload?: {
  timeout_ms: number;
  pattern_conditions: string[];
  break_down_by: string[];
  time_range: string[];
  session_level_filter?: string;
}) => {
  return useQuery({
    queryKey: ['dataByConditions', payload],
    queryFn: async () => {
      const res = await getDataByConditions();
      const traceId = res.config.headers['x-cvv-trace-id'];
      return { data: res?.data, customHeader: { traceId } };
    },
    enabled:
      !!payload &&
      payload.pattern_conditions?.length > 0 &&
      payload.pattern_conditions?.filter((v) => !v).length === 0,
    onError: (error: any) => {
      if (error?.response?.status === 500) {
        message.error('Internal server error, please try again later');
      }
    },
  });
};

export const useGetCriticalEvent = (id?: string) => {
  return useQuery({
    queryKey: ['critical-event', id],
    queryFn: async () => {
      if (!id) return null;
      const res = await getCriticalEvent(id);
      return res?.data?.critical_event || null;
    },
    enabled: !!id,
    cacheTime: 0,
    staleTime: 0,
    onError: (error: any) => {
      console.error('Critical event fetch failed:', error);
      if (error?.response?.status === 500) {
        message.error('Internal server error, please try again later');
      } else if (error?.response?.status === 404) {
        message.error('Critical event not found');
      } else {
        message.error('Failed to fetch critical event');
      }
    },
  });
};

export const useCreateCriticalEvent = () => {
  return useMutation({
    mutationFn: async (data: CreateCriticalEventRequest) => {
      const res = await createCriticalEvent(data);
      return res?.data;
    },
    onSuccess: async (data) => {
      message.success(data?.message || 'Critical event created successfully');
      // Invalidate and wait for refetch to complete
      await queryClient.invalidateQueries({ queryKey: ['critical-events'] });
    },
    onError: (error: any) => {
      console.error('Critical event creation failed:', error);
      if (error?.response?.status === 500) {
        message.error('Internal server error, please try again later');
      } else if (error?.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to create critical event');
      }
    },
  });
};

export const useUpdateCriticalEvent = () => {
  return useMutation({
    mutationFn: async ({ ceId, data }: { ceId: string; data: UpdateCriticalEventRequest }) => {
      const res = await updateCriticalEvent(ceId, data);
      return res?.data;
    },
    onSuccess: (data) => {
      message.success(data?.message || 'Critical event updated successfully');
    },
    onError: (error: any) => {
      console.error('Critical event update failed:', error);
      if (error?.response?.status === 500) {
        message.error('Internal server error, please try again later');
      } else if (error?.response?.status === 404) {
        message.error('Critical event not found');
      } else if (error?.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to update critical event');
      }
    },
  });
};

export const useGetDimension = (dimensionId?: string) => {
  return useQuery({
    queryKey: ['dimension', dimensionId],
    queryFn: async () => {
      if (!dimensionId) return null;
      const res = await getDimension(dimensionId);
      return res?.data || null;
    },
    enabled: !!dimensionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error: any) => {
      console.error('Dimension fetch failed:', error);
      if (error?.response?.status === 500) {
        message.error('Internal server error, please try again later');
      } else if (error?.response?.status === 404) {
        message.error('Dimension not found');
      } else {
        message.error('Failed to fetch dimension');
      }
    },
  });
};

export const useCreateDimension = () => {
  return useMutation({
    mutationFn: async (data: CreateDimensionRequest) => {
      const res = await createDimension(data);
      return res?.data;
    },
    onSuccess: async () => {
      message.success('Dimension created successfully');
      // Invalidate and wait for refetch to complete
      await queryClient.invalidateQueries({ queryKey: ['dimensions'] });
    },
    onError: (error: any) => {
      console.error('Dimension creation failed:', error);
      if (error?.response?.status === 500) {
        message.error('Internal server error, please try again later');
      } else if (error?.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to create dimension');
      }
    },
  });
};

export const useUpdateDimension = () => {
  return useMutation({
    mutationFn: async ({ cdId, data }: { cdId: string; data: UpdateDimensionRequest }) => {
      const res = await updateDimension(cdId, data);
      return res?.data;
    },
    onSuccess: async (data, variables) => {
      message.success(data?.message || 'Dimension updated successfully');
      // Invalidate and wait for refetch to complete
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dimension', variables.cdId] }),
        queryClient.invalidateQueries({ queryKey: ['dimensions'] }),
      ]);
    },
    onError: (error: any) => {
      console.error('Dimension update failed:', error);
      if (error?.response?.status === 500) {
        message.error('Internal server error, please try again later');
      } else if (error?.response?.status === 404) {
        message.error('Dimension not found');
      } else if (error?.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to update dimension');
      }
    },
  });
};

export const usePostUserJourneysFilter = (
  payload?: UserJourneyFilterRequest,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['userJourneysFilter', payload],
    queryFn: async () => {
      if (!payload) throw new Error('Payload is required');
      try {
        const res = await postUserJourneysFilter(payload);
        return res?.data;
      } catch (error: any) {
        console.error('User journeys filter failed:', error);
        if (error?.response?.status === 500) {
          message.error('Internal server error, please try again later');
        } else {
          message.error('Failed to fetch user journeys');
        }
        throw error;
      }
    },
    enabled: options?.enabled !== false && !!payload,
    retry: 1,
  });
};

export const useGetPatterns = (payload?: PatternsRequest, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['patterns', payload],
    queryFn: async () => {
      if (!payload) throw new Error('Payload is required');
      try {
        const res = await getPatterns(payload);
        return res?.data?.patterns || [];
      } catch (error: any) {
        console.error('Patterns fetch failed:', error);
        message.error('Failed to fetch patterns');
        throw error;
      }
    },
    enabled: options?.enabled !== false && !!payload,
    retry: 1,
  });
};

export const usePostPatternQuery = (payload?: PatternQueryRequest) => {
  return useQuery({
    queryKey: ['pattern-query', payload],
    queryFn: async () => {
      if (!payload) return [];
      const res = await postPatternQuery(payload);
      return res?.data?.data || [];
    },
    enabled: !!payload && !!payload.pattern && payload.pattern.length > 0,
    staleTime: 3 * 60 * 1000, // 3 minutes - pattern metrics are relatively stable
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    onError: (error: any) => {
      console.error('Pattern query failed:', error);
      message.error('Failed to fetch pattern metrics');
    },
  });
};

// Alternative mutation-based hook for one-off pattern queries
export const usePostPatternQueryMutation = () => {
  return useMutation({
    mutationFn: async (payload: PatternQueryRequest) => {
      const res = await postPatternQuery(payload);
      return res?.data?.data || [];
    },
    onError: (error: any) => {
      console.error('Pattern query failed:', error);
      message.error('Failed to fetch pattern metrics');
    },
  });
};

export const usePostPatternsQuery = (payload?: PatternsQueryRequest) => {
  return useQuery({
    queryKey: ['patterns-query', payload],
    queryFn: async () => {
      if (!payload) return {};
      const res = await postPatternsQuery(payload);
      return res?.data || {};
    },
    enabled:
      !!payload &&
      !!payload.patterns &&
      payload.patterns.length > 0 &&
      !!payload.time_range &&
      !!payload.time_range[0] &&
      !!payload.time_range[1],
    staleTime: 3 * 60 * 1000, // 3 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    onError: (error: any) => {
      console.error('Patterns query failed:', error);
      message.error('Failed to fetch patterns metrics');
    },
  });
};

export const usePostMilestoneQuery = (payload?: MilestoneQueryRequest) => {
  return useQuery({
    queryKey: ['milestone-query', payload],
    queryFn: async () => {
      if (!payload) return [];
      const res = await postMilestoneQuery(payload);
      return res?.data?.data || [];
    },
    enabled: !!payload && !!payload.milestone && !!payload.time_range,
    staleTime: 3 * 60 * 1000, // 3 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    onError: (error: any) => {
      console.error('Milestone query failed:', error);
      message.error('Failed to fetch milestone metrics');
    },
  });
};

// Alternative mutation-based hook for one-off milestone queries
export const usePostMilestoneQueryMutation = () => {
  return useMutation({
    mutationFn: async (payload: MilestoneQueryRequest) => {
      const res = await postMilestoneQuery(payload);
      return res?.data?.data || [];
    },
    onError: (error: any) => {
      console.error('Milestone query failed:', error);
      message.error('Failed to fetch milestone metrics');
    },
  });
};

/**
 * Hook to fetch event names from metadata
 * @param payload - Request payload with time_range and key
 * @returns Query result with event_names array
 */
export const useGetEventNames = (payload: GetEventNamesRequest) => {
  return useQuery({
    queryKey: ['event-names', payload],
    queryFn: async () => {
      return await getEventNames(payload);
    },
    enabled: !!payload,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    onError: (error: any) => {
      console.error('Failed to fetch event names:', error);
      message.error('Failed to fetch event names');
    },
  });
};

/**
 * Hook to fetch tag keys from metadata
 * @param payload - Request payload with time_range and key for search/filter
 * @returns Query result with tag_keys array
 */
export const useGetTagKeys = (payload?: GetTagKeysRequest, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['tag-keys', payload],
    queryFn: async () => {
      if (!payload) return [];
      return await getTagKeys(payload);
    },
    enabled: options?.enabled !== false && !!payload,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    onError: (error: any) => {
      console.error('Failed to fetch tag keys:', error);
      message.error('Failed to fetch tag keys');
    },
  });
};

/**
 * Hook to fetch tag values from metadata
 * @param payload - Request payload with time_range, tag_key, and key for search/filter
 * @param options - Optional query options including enabled flag
 * @returns Query result with tag_values array
 */
export const useGetTagValues = (payload: GetTagValuesRequest, options?: { enabled?: boolean }) => {
  const { enabled: externalEnabled = true } = options || {};
  return useQuery({
    queryKey: ['tag-values', payload],
    queryFn: async () => {
      if (!payload) return [];
      return await getTagValues(payload);
    },
    enabled: externalEnabled && !!payload && !!payload.tag_key,
    cacheTime: 10 * 1000,
    staleTime: 10 * 1000,
    retry: 2,
    onError: (error: any) => {
      console.error('Failed to fetch tag values:', error);
      message.error('Failed to fetch tag values');
    },
  });
};
